import type { InputAction, PlayerInput } from "@dyson/shared";
import { DEFAULT_KEYS, type Action } from "./bindings";
import { TouchControls } from "./Touch";

export type InputAxes = Omit<PlayerInput, "seq" | "dt" | "aimX" | "aimY" | "fire" | "hold">;

const CAPTURED = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab"]);
/** Stick values below this count as centred. */
const DEADZONE = 0.25;
/** How fast the right stick moves the cursor, pixels a second at full tilt. */
const PAD_CURSOR_SPEED = 900;

/**
 * Standard gamepad layout (as browsers map it): which button does what.
 * Triggers are the mouse buttons, bumpers turn the ship, A jumps, B leaves the helm or a turret.
 */
const PAD = { a: 0, b: 1, x: 2, y: 3, lb: 4, rb: 5, lt: 6, rt: 7, back: 8, start: 9, ls: 10, rs: 11, up: 12, down: 13, left: 14, right: 15 } as const;
/** Pad buttons that work like a key press of an action. */
const PAD_ACTIONS: Partial<Record<number, Action>> = {
  [PAD.y]: "inventory",
  [PAD.start]: "settings",
  [PAD.rs]: "hook",
};

/** Keyboard, mouse, gamepad and touch state, sampled once per simulation step. */
export class Input {
  /** The touch controls (shown on touch screens, see Settings.touch): read here like the pad. */
  readonly touch: TouchControls;
  private readonly down = new Set<string>();
  /** Keys pressed since the last sample, so a quick tap is never missed. */
  private readonly pressed = new Set<string>();
  /** Actions pressed since the last sample (keys, pad buttons or touch buttons). */
  private readonly actions = new Set<Action>();
  private keys: Record<Action, string[]> = DEFAULT_KEYS;
  private padButtons: boolean[] = [];
  private lastPadAt = performance.now();
  private padFire = false;
  private padUse = false;
  /** Right click took (or left) the helm or a turret: goes with the next step. */
  private interactQueued = false;
  private grappleQueued = false;

  /** The pointer (mouse, pad cursor, or the last finger on the world), in client px. */
  mouseX = window.innerWidth / 2;
  mouseY = window.innerHeight / 2;
  private mouseLeft = false;
  private mouseRight = false;
  /** Ctrl (or Cmd) is held, for Ctrl+Z. */
  ctrl = false;

  /** One-shot key presses the game handles outside the simulation (build mode, hotbar, respawn). */
  onKey: (e: KeyboardEvent) => void = () => {};
  /** A pad or touch button that works like an action key was pressed. */
  onPadAction: (action: Action) => void = () => {};
  /** The pad's d-pad left or right: step the quick-access bar. */
  onPadSlot: (step: number) => void = () => {};
  /** Mouse button pressed or moved over the canvas (or a finger on it). */
  onPointer: () => void = () => {};
  /** Something other than the game wants the keys (photo mode pans the camera). */
  freeze = false;

  constructor(canvas: HTMLCanvasElement, hud: HTMLElement, target: Window = window) {
    this.touch = new TouchControls(hud);
    this.touch.onLeave = () => this.interact();
    this.touch.onPress = (a) => {
      this.actions.add(a);
      this.onPadAction(a);
    };
    this.touch.onPointer = (x, y) => {
      this.mouseX = x;
      this.mouseY = y;
      this.onPointer();
    };

    target.addEventListener("keydown", (e) => {
      if (isTyping(e)) return;
      if (CAPTURED.has(e.code)) e.preventDefault();
      this.ctrl = e.ctrlKey || e.metaKey;
      if (e.repeat) return;
      this.down.add(e.code);
      this.pressed.add(e.code);
      for (const a of this.actionsOf(e.code)) this.actions.add(a);
      this.onKey(e);
    });
    target.addEventListener("keyup", (e) => {
      this.down.delete(e.code);
      this.ctrl = e.ctrlKey || e.metaKey;
    });
    target.addEventListener("blur", () => {
      this.down.clear();
      this.mouseLeft = this.mouseRight = false;
    });

    // Pointer events, so a finger is told from the mouse (a tap would otherwise also arrive as a click).
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    canvas.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "touch") return this.touch.fingerDown(e);
      if (e.button === 0) this.mouseLeft = true;
      if (e.button === 2) this.mouseRight = true;
      this.onPointer();
    });
    target.addEventListener("pointerup", (e) => {
      if (e.pointerType === "touch") return this.touch.fingerUp(e);
      if (e.button === 0) this.mouseLeft = false;
      if (e.button === 2) this.mouseRight = false;
    });
    target.addEventListener("pointercancel", (e) => {
      if (e.pointerType === "touch") this.touch.fingerUp(e);
    });
    canvas.addEventListener("pointermove", (e) => {
      if (e.pointerType === "touch") return this.touch.fingerMove(e);
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
      this.onPointer();
    });
  }

  /** Left button (or the pad's right trigger, or touch's fire button or digging finger) held. */
  get leftDown(): boolean {
    return this.mouseLeft || this.padFire || this.touch.leftDown;
  }

  /** Right button (or the pad's left trigger, or touch's placing finger) held. */
  get rightDown(): boolean {
    return this.mouseRight || this.padUse || this.touch.rightDown;
  }

  /**
   * On touch, walking with no finger on the world: which way the stick
   * points (screen axes, y up), for the head and tool to follow. Null when
   * the pointer should be used as it is.
   */
  aimHint(): { x: number; y: number } | null {
    const t = this.touch;
    if (!t.enabled || t.mode !== "foot" || t.worldDown || !t.stickActive) return null;
    const len = Math.hypot(t.move.x, t.move.y);
    return { x: t.move.x / len, y: t.move.y / len };
  }

  /** Take the helm or turret seat you stand at, or leave it, on the next step (right click, or touch's leave buttons). */
  interact(): void {
    this.interactQueued = true;
  }

  /** Throw the grapple, or let go of its line, on the next step (right click holding it). */
  grapple(): void {
    this.grappleQueued = true;
  }

  setKeys(keys: Record<Action, string[]>): void {
    this.keys = keys;
  }

  /** Every action a key code is bound to. */
  actionsOf(code: string): Action[] {
    return (Object.keys(this.keys) as Action[]).filter((a) => this.keys[a].includes(code));
  }

  /** Is any key for `action` held? */
  held(action: Action): boolean {
    return !this.freeze && this.keys[action].some((c) => this.down.has(c));
  }

  /** Is a raw key code held (for photo mode's panning, which isn't rebindable)? */
  codeHeld(code: string): boolean {
    return this.down.has(code);
  }

  private axis(neg: Action, pos: Action): number {
    return (this.held(pos) ? 1 : 0) - (this.held(neg) ? 1 : 0);
  }

  /**
   * The gamepad, read once a step: the left stick walks (or flies), A jumps,
   * the bumpers turn the ship, the right stick moves the cursor, triggers are
   * the mouse buttons. Returns its move axes (0 with no pad).
   */
  private pollPad(): { x: number; y: number; turn: number; jump: boolean; leave: boolean } {
    const none = { x: 0, y: 0, turn: 0, jump: false, leave: false };
    const pads = typeof navigator.getGamepads === "function" ? navigator.getGamepads() : [];
    const pad = [...pads].find((p): p is Gamepad => !!p && p.connected);
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastPadAt) / 1000);
    this.lastPadAt = now;
    if (!pad) {
      this.padFire = this.padUse = false;
      return none;
    }
    const dead = (v: number | undefined) => (v === undefined || Math.abs(v) < DEADZONE ? 0 : v);
    const pressed = (i: number) => !!pad.buttons[i]?.pressed;
    const edge = (i: number) => pressed(i) && !this.padButtons[i];
    for (const [i, action] of Object.entries(PAD_ACTIONS)) if (action && edge(Number(i))) {
      this.actions.add(action);
      this.onPadAction(action);
    }
    if (edge(PAD.left)) this.onPadSlot(-1);
    if (edge(PAD.right)) this.onPadSlot(1);
    const rx = dead(pad.axes[2]);
    const ry = dead(pad.axes[3]);
    if (rx || ry) {
      this.mouseX = Math.min(window.innerWidth, Math.max(0, this.mouseX + rx * PAD_CURSOR_SPEED * dt));
      this.mouseY = Math.min(window.innerHeight, Math.max(0, this.mouseY + ry * PAD_CURSOR_SPEED * dt));
      this.onPointer();
    }
    const use = pressed(PAD.lt);
    if (use !== this.padUse) {
      this.padUse = use;
      this.onPointer();
    }
    this.padFire = pressed(PAD.rt);
    const out = {
      x: dead(pad.axes[0]),
      y: -dead(pad.axes[1]),
      turn: (pressed(PAD.lb) ? 1 : 0) - (pressed(PAD.rb) ? 1 : 0),
      jump: pressed(PAD.a),
      leave: edge(PAD.b),
    };
    this.padButtons = pad.buttons.map((b) => b.pressed);
    return out;
  }

  /**
   * The keys since the last step. At the helm Q/E turn; a right click (see
   * interact) takes or leaves the helm or a turret. One action pressed this
   * step rides along: G (vines, the ship's grapple), else a right click
   * holding the grapple (see grapple). The pad and the touch stick and
   * buttons add to the same axes.
   */
  sample(piloting: boolean): InputAxes {
    const pad = this.pollPad();
    const t = this.touch;
    const moveX = clampUnit(this.axis("left", "right") + pad.x + t.move.x);
    const moveY = clampUnit(this.axis("down", "up") + pad.y + (pad.jump ? 1 : 0) + t.moveY + (t.isHeld("jump") ? 1 : 0));
    // At the helm Q/E turn the ship; on foot they spin you in open space (the sim ignores them elsewhere).
    const turn = clampUnit(this.axis("turnRight", "turnLeft") + pad.turn + (t.isHeld("turnLeft") ? 1 : 0) - (t.isHeld("turnRight") ? 1 : 0));
    const was = (a: Action) => !this.freeze && this.actions.has(a);
    const action: InputAction | null = was("hook") ? "hook" : !this.freeze && this.grappleQueued ? "grapple" : null;
    const out: InputAxes = {
      moveX,
      moveY,
      turn,
      interact: !this.freeze && (this.interactQueued || pad.leave),
      action,
    };
    this.interactQueued = false;
    this.grappleQueued = false;
    this.pressed.clear();
    this.actions.clear();
    return out;
  }
}

const clampUnit = (v: number) => Math.max(-1, Math.min(1, v));

function isTyping(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
}
